/**
 * SINGLE-KEY ACTS ON THE SCREEN YOU ARE STANDING ON.
 *
 * The pick and the check are the two screens on this platform somebody works
 * with both hands full — a roving grid, a letter per act, and the letters
 * printed under the table. Everywhere else the hand left the desk: take payment,
 * add a patient, place an order, call the next ticket, start a note. Same staff,
 * same shift, same dispensary systems they came off, where the work is ↓ ↓
 * Enter.
 *
 * It is ONE document listener, installed once at the app root beside the
 * primary-action one. A screen never adds its own: a second listener on `window`
 * beside this one on `document` fires the same letter twice.
 *
 * ── FOUR RULES, EACH A BUG THAT WOULD OTHERWISE HAPPEN ──────────────────────
 *
 * A KEY IS REGISTERED WHERE ITS CONTROL IS RENDERED, FROM THE SAME CONDITION.
 * A button with a hotkey registers itself and clicks ITSELF; unmounted is
 * unbound, disabled is printed-but-dead. So the permission that hides the button
 * hides its key, and there is no second gate to keep in step.
 *
 * A KEY FIRES ONLY WITH NOTHING TO TYPE INTO AND NOTHING ON TOP. Not from a box,
 * not behind a dialogue unless the binding is INSIDE that dialogue, never with a
 * modifier, never mid-IME, never on auto-repeat, and never inside a BARCODE
 * BURST. That last is not hypothetical: a USB scanner is a keyboard, the POS is
 * what it is plugged into, and thirteen digits at an unfocused page would
 * otherwise press four different buttons.
 *
 * A KEY ON A ROW BEATS A KEY ON THE SCREEN. React's handlers are delegated at
 * the root, which every event passes before reaching this listener, so a grid or
 * rail row that consumed a key has already called `preventDefault` — and this
 * bails on `defaultPrevented`. No priority table and no registration order to
 * get right: the DOM answers it.
 *
 * AND EVERY KEY IS PRINTED — on its control, and in the `?` sheet. An
 * undiscoverable shortcut is a shortcut for whoever wrote it.
 *
 * THE LABEL IS A MESSAGE KEY, not a sentence. The POC carried both languages on
 * the binding; here the words are rows in the table under `hotkey.*`, so the
 * sheet reads the terminal's language and a third one is a column.
 */
import { useEffect, useId, useRef, useSyncExternalStore } from "react";
import type { MessageKey } from "@/i18n/resources.gen";
import { createRegistry } from "./registry";
import { overlayDepth, subscribeOverlays, topOverlay } from "./overlay-stack";
import { MOD_ENTER, MOD_KEY } from "./platform";
import { primaryActionIn, primaryActionScope } from "./primary-action";
import { isTypingTarget, isVisible } from "./typing-target";

/** Which of the `?` sheet's three lists a binding belongs in. */
export type HotkeyGroup = "screen" | "list" | "everywhere";

/**
 * What a binding says it does — a row under `hotkey.*` in the message table.
 *
 * Narrow on purpose, and it is a convention worth keeping: every wording a key
 * carries lives in one family, so the sheet reads one part of the table and its
 * rows can be reviewed together. It also keeps the options type honest, `t`
 * being typed against every key at once.
 */
export type HotkeyMessageKey = Extract<MessageKey, `hotkey.${string}`>;

export interface HotkeyBinding {
  /**
   * Per MOUNT, not per key: two buttons on one screen may legitimately carry the
   * same letter, and registration is idempotent by id — keyed on the key, the
   * second would evict the first.
   */
  id: string;
  /** What the operator presses: a single `a–z` / `0–9` / `/`, or a named key. */
  key: string;
  /** What it does, as a row in the message table. */
  label: HotkeyMessageKey;
  /** Values the label interpolates, where it has any. */
  labelParams?: { mod?: string };
  run: () => void;
  /** False draws the key dimmed and refuses to fire it — a disabled button's
   *  letter. Absent is live. */
  when?: boolean;
  group: HotkeyGroup;
  /**
   * WHICH SURFACE THIS KEY BELONGS TO — the covering overlay, or the page.
   *
   * It is what lets a dialogue's own buttons keep their keys while the screen
   * behind goes quiet, without either side knowing about the other.
   */
  scope: ParentNode | null;
  /**
   * PRINTED BUT NOT DISPATCHED — a key somebody else already owns.
   *
   * ⌘K is the palette's, ⌘/Ctrl+Enter is the primary action's, ↑ ↓ belong to
   * whichever list has focus. They are registered so the sheet can answer "what
   * can I press here" completely; dispatching them here would be a second
   * listener for a key that already has one.
   */
  passive?: boolean;
  order?: number;
}

const registry = createRegistry<HotkeyBinding>({ ordered: true });

/** Register one binding. Idempotent by id; returns the unregister. */
export function registerScreenHotkey(binding: HotkeyBinding): () => void {
  return registry.register(binding);
}

export function screenHotkeys(): readonly HotkeyBinding[] {
  return registry.list();
}

export function useScreenHotkeyList(): readonly HotkeyBinding[] {
  return registry.use();
}

// ── The ones that never change ──────────────────────────────────────────────
//
// Registered here, at module load, and all of them together: they are ONE
// vocabulary — the keys somebody learns in their first week and never looks up
// again — and each already has a listener older than this registry, so every one
// is `passive`. What they are doing here is being ANSWERABLE: a sheet that
// omitted ⌘K because ⌘K predates it would send the reader back to guessing.
//
// TAB IS THE BROWSER'S AND IS LISTED ANYWAY. Nothing binds it and nothing
// should, but "who implemented it" is not the question somebody asking "what can
// I press" is asking, and Tab is how you REACH the control a letter is printed
// on. Documenting what we do not own is safe only because this cannot drift: a
// browser with no Tab is not a browser.

/** The one built-in whose liveness depends on the screen — see `refreshPrimaryRow`. */
const PRIMARY_ROW = "everywhere:primary";

const noop = (): void => {
  /* dispatched elsewhere — see `passive` */
};

const BUILT_INS: HotkeyBinding[] = [
  {
    id: "everywhere:search",
    key: `${MOD_KEY} K`,
    label: "hotkey.search",
    group: "everywhere",
    passive: true,
    scope: null,
    order: 10,
    run: noop,
  },
  {
    id: PRIMARY_ROW,
    key: MOD_ENTER,
    label: "hotkey.primary",
    group: "everywhere",
    passive: true,
    scope: null,
    order: 20,
    run: noop,
  },
  {
    id: "everywhere:tab",
    key: "Tab ⇧Tab",
    label: "hotkey.tab",
    group: "everywhere",
    passive: true,
    scope: null,
    order: 25,
    run: noop,
  },
  {
    id: "everywhere:searchBox",
    key: "/",
    label: "hotkey.searchBox",
    group: "everywhere",
    passive: true,
    scope: null,
    order: 40,
    run: noop,
  },
  {
    id: "everywhere:escape",
    key: "Escape",
    label: "hotkey.escape",
    group: "everywhere",
    passive: true,
    scope: null,
    order: 50,
    run: noop,
  },
  {
    id: "everywhere:sheet",
    key: "?",
    label: "hotkey.sheet",
    group: "everywhere",
    passive: true,
    scope: null,
    order: 60,
    run: noop,
  },
];

function registerBuiltIns(): void {
  for (const b of BUILT_INS) registry.register(b);
}

registerBuiltIns();

/** For tests: back to the built-ins and nothing else. */
export function resetScreenHotkeys(): void {
  registry.reset();
  registerBuiltIns();
}

/**
 * HOW A LIST MOVES — one wording, for every list on the platform.
 *
 * Three things put rows on a screen — a rail, a table, a data grid — and all
 * three are walked identically. Registering the pair separately in each one's
 * own words means the sentence a reader gets is whichever mounted first, so a
 * screen holding a rail beside a grid described its own arrow keys by accident
 * of render order.
 *
 * HOME AND END ARE HERE BECAUSE THEY WORK, and a key that does something and is
 * not on the list is a key nobody presses. The label carries the laptop's answer
 * too: a MacBook has no Home key and neither does most of the compact Windows
 * estate, where both spell `Fn + ←`, which no chip can print — so the jump also
 * answers to ⌘/Ctrl + ↑ ↓, in the modifier the reader's own keyboard prints.
 */
export const LIST_MOVE_BINDINGS: Omit<HotkeyBinding, "id">[] = [
  {
    key: "↑ ↓",
    label: "hotkey.listMove",
    group: "list",
    passive: true,
    scope: null,
    order: 10,
    run: noop,
  },
  {
    key: "Home End",
    label: "hotkey.listEnds",
    labelParams: { mod: MOD_KEY },
    group: "list",
    passive: true,
    scope: null,
    order: 15,
    run: noop,
  },
  {
    key: "Enter",
    label: "hotkey.listOpen",
    group: "list",
    passive: true,
    scope: null,
    order: 20,
    run: noop,
  },
];

// ── The pure half — everything a node test can reach ────────────────────────

/**
 * Keys a button may claim. A named key is for the hook and never a button: a
 * printed `Enter` on a control that Enter already activates says nothing.
 */
export const BUTTON_KEY_RE = /^[a-z0-9/]$/;

/**
 * A SCANNER IS A KEYBOARD, and this is how we tell them apart.
 *
 * Keystrokes closer together than this are a machine: a USB barcode scanner
 * emits a whole code at 5–15 ms per character, where the fastest typing anybody
 * does at a counter is nearer 100. We only need to know the key was not pressed
 * by a finger, so the threshold is tight enough that a fast double-tap of two
 * different letters still gets through.
 */
export const BURST_MS = 30;

/** Keys the hook may bind that are not characters. Deliberately short: anything
 *  longer is a key some control on the page already owns. */
const NAMED_KEYS = new Set(["ArrowLeft", "ArrowRight"]);

/**
 * The key this event means, or null where it means nothing to us.
 *
 * `e.key` and not `e.code`: `?` is Shift+/ on the layout Hong Kong types on, and
 * `e.code` calls it `Slash`, which is a different key with a different job here.
 * CapsLock reports `"N"` with `shiftKey` false, so letters are lowercased rather
 * than rejected — a caps-locked keyboard is a Tuesday at a counter, not a
 * decision. Shift is otherwise refused, which keeps `!` off the digit strip.
 */
export function normaliseKey(e: KeyboardEvent, gapMs = Number.POSITIVE_INFINITY): string | null {
  if (e.metaKey || e.ctrlKey || e.altKey) return null;
  // Mid-composition (中文輸入) every keystroke belongs to the candidate window.
  if (e.isComposing || e.key === "Process" || e.key === "Dead") return null;
  // A HELD KEY IS ONE PRESS. Without this, leaning on a letter strobes whatever
  // it opens thirty times a second.
  if (e.repeat) return null;

  if (NAMED_KEYS.has(e.key)) return e.shiftKey ? null : e.key;
  if (e.key.length !== 1) return null;
  // The burst guard is for PRINTABLE keys only — a scanner sends characters,
  // never arrows.
  if (gapMs < BURST_MS) return null;
  if (e.key === "?" || e.key === "/") return e.key;
  if (e.shiftKey) return null;
  const k = e.key.toLowerCase();
  return /^[a-z0-9]$/.test(k) ? k : null;
}

/**
 * Which binding a key reaches in a scope — the first live, non-passive one
 * registered against that surface.
 *
 * FIRST AND NOT LAST, so a screen drawing one act twice (a header button and the
 * same act at the foot of a table) presses the one nearer the top of the
 * document, which is the one the reader is looking at. Both stay registered, and
 * the sheet dedupes them.
 */
export function pickBinding(
  list: readonly HotkeyBinding[],
  key: string,
  scope: ParentNode | null,
): HotkeyBinding | null {
  if (!scope) return null;
  return list.find((b) => b.key === key && b.scope === scope && b.when !== false && !b.passive) ?? null;
}

/**
 * Keys claimed twice on one surface by two different acts.
 *
 * Two bindings of one key with the SAME label are one act drawn twice and are
 * fine. Two with different labels are a screen where one of them can never be
 * pressed, which is the failure this mechanism exists to make impossible —
 * reported in development and asserted in the browser suite, because a screen is
 * a composition and no source scan can see what is mounted together.
 */
export function collisions(list: readonly HotkeyBinding[]): { key: string; labels: string[] }[] {
  // ␟ and not a space, because a key may CONTAIN one: `⌘ K` and `↑ ↓` are each
  // a single binding, and splitting a composite id on " " hands back `⌘`.
  const seen = new Map<string, Set<string>>();
  for (const b of list) {
    if (b.passive || b.when === false) continue;
    const at = `${b.key}␟${scopeId(b.scope)}`;
    const labels = seen.get(at) ?? new Set<string>();
    labels.add(b.label);
    seen.set(at, labels);
  }
  return [...seen.entries()]
    .filter(([, labels]) => labels.size > 1)
    .map(([at, labels]) => ({ key: at.split("␟")[0] ?? "", labels: [...labels] }));
}

let scopeSeq = 0;
const scopeIds = new WeakMap<ParentNode, string>();

/** A stable name for a surface, for grouping and for messages. */
function scopeId(scope: ParentNode | null): string {
  if (!scope) return "none";
  if (typeof document !== "undefined" && scope === document.body) return "body";
  let id = scopeIds.get(scope);
  if (!id) {
    scopeSeq += 1;
    id = `overlay-${scopeSeq}`;
    scopeIds.set(scope, id);
  }
  return id;
}

// ── The `?` sheet's open state ──────────────────────────────────────────────
// A boolean read by a sibling of whatever sets it, because the DISPATCHER opens
// the sheet and the dispatcher cannot import a component.

let sheetOpen = false;
const sheetListeners = new Set<() => void>();

function emitSheet(): void {
  for (const fn of sheetListeners) fn();
}

function subscribeSheet(listener: () => void): () => void {
  sheetListeners.add(listener);
  return () => {
    sheetListeners.delete(listener);
  };
}

const readSheet = (): boolean => sheetOpen;

/**
 * DOES ⌘/Ctrl+Enter DO ANYTHING ON THE SCREEN BEHIND THIS SHEET?
 *
 * Asked at the moment of opening, and it has to be: once the sheet is up it is
 * itself the covering surface, so the scope the real handler would resolve is
 * gone. Most screens declare no primary action, so listed flat the one modifier
 * shortcut on the platform read as broken — press it and nothing happens, under
 * a heading saying it works everywhere. The binding was right; the promise was
 * not. Dormant is PRINTED, never removed: a row that vanished would read as a
 * key this build has not got.
 */
function refreshPrimaryRow(): void {
  const row = BUILT_INS.find((r) => r.id === PRIMARY_ROW);
  if (!row || typeof document === "undefined") return;
  registry.register({ ...row, when: primaryActionIn(primaryActionScope()) !== null });
}

export function openHotkeySheet(): void {
  if (sheetOpen) return;
  refreshPrimaryRow();
  sheetOpen = true;
  emitSheet();
}

export function closeHotkeySheet(): void {
  if (!sheetOpen) return;
  sheetOpen = false;
  emitSheet();
}

export function toggleHotkeySheet(): void {
  if (sheetOpen) closeHotkeySheet();
  else openHotkeySheet();
}

export function useHotkeySheetOpen(): boolean {
  return useSyncExternalStore(subscribeSheet, readSheet, readSheet);
}

// ── Registering, from a component ───────────────────────────────────────────

type BindingInput = Omit<HotkeyBinding, "id" | "scope"> & { scope?: ParentNode | null };

const EMPTY: BindingInput[] = [];

/**
 * Bind one key for as long as this component is mounted.
 *
 * `null` unbinds — pass it rather than calling the hook conditionally.
 */
export function useScreenHotkey(binding: BindingInput | null): void {
  const id = useId();
  useScreenHotkeys(binding ? [binding] : EMPTY, id);
}

/**
 * The same for a set whose length varies — a tab strip, a step strip, two
 * asides. One effect over the lot, keyed on what is actually in them.
 */
export function useScreenHotkeys(bindings: BindingInput[], idPrefix?: string): void {
  const auto = useId();
  const prefix = idPrefix ?? auto;

  /**
   * THE HANDLERS, FRESH EVERY RENDER, behind a ref the registration effect never
   * depends on. Without it a binding whose `run` closes over current props —
   * which is all of them — re-registers on every render, and everything reading
   * the registry re-renders with it.
   *
   * WRITTEN IN A COMMIT, NOT DURING THE RENDER: a render React throws away must
   * leave nothing behind. Declared before the registration effect so it runs
   * first on every commit.
   */
  const runs = useRef<(() => void)[]>(bindings.map((b) => b.run));
  useEffect(() => {
    runs.current = bindings.map((b) => b.run);
  });

  // What the effect depends on: the shape of the set, not its closures.
  const shape = bindings
    .map((b) => `${b.key}|${b.when === false ? 0 : 1}|${b.group}|${b.label}|${b.passive === true ? 1 : 0}`)
    .join("␟");

  useEffect(() => {
    const offs = bindings.map((b, i) =>
      registerScreenHotkey({
        ...b,
        id: `${prefix}:${i}`,
        scope: b.scope ?? (typeof document === "undefined" ? null : document.body),
        run: () => runs.current[i]?.(),
      }),
    );
    return () => {
      for (const off of offs) off();
    };
    // `shape` is the dependency: the array is rebuilt every render and the runs
    // are read through the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape, prefix]);
}

// ── The dispatcher ──────────────────────────────────────────────────────────

/**
 * WHICH SURFACE IS ON TOP, as a scope bindings can be matched against.
 *
 * The page when nothing covers it; otherwise the newest overlay still on screen.
 * A dialogue that registers no keys therefore answers itself and swallows every
 * letter, which is the right answer: its own keys are the only ones its reader
 * can mean.
 */
export function topScope(): ParentNode | null {
  if (typeof document === "undefined") return null;
  if (overlayDepth() === 0) return document.body;
  return topOverlay();
}

/** The box a `/` jumps into on this surface, if it has one. */
function searchBoxIn(scope: ParentNode | null): HTMLElement | null {
  if (!scope) return null;
  const el = scope.querySelector<HTMLElement>("[data-search]");
  return el && isVisible(el) ? el : null;
}

/** True where this surface offers a `/`. The sheet asks, so it does not promise
 *  a key the screen has not got. */
export function scopeHasSearch(): boolean {
  return searchBoxIn(topScope()) !== null;
}

/**
 * The one document listener. Installed once, at the app root.
 *
 * ORDER MATTERS AND IS ARGUED, top to bottom:
 *   1. somebody else has handled it (a grid row, a combobox) — leave;
 *   2. Escape in a box gives the box back, so the operator can reach the letters
 *      again without a mouse;
 *   3. anything else typed into a box is a character, full stop;
 *   4. `?` opens the sheet BEFORE the overlay gate, because "what can I press"
 *      is worth answering over a dialogue too;
 *   5. `/` reaches for this surface's search box;
 *   6. and only then does a registered key fire.
 */
export function useScreenHotkeyDispatcher(): void {
  useEffect(() => {
    let lastAt = Number.NEGATIVE_INFINITY;

    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;

      if (isTypingTarget(e)) {
        // ESCAPE HANDS THE KEYBOARD BACK, and only with nothing over the screen:
        // inside a dialogue Escape belongs to the dialogue, and blurring its
        // field instead would leave the reader pressing Escape twice with
        // nothing visibly happening on the first.
        if (e.key === "Escape" && overlayDepth() === 0 && e.target instanceof HTMLElement) {
          e.target.blur();
        }
        lastAt = e.timeStamp;
        return;
      }

      const gap = e.timeStamp - lastAt;
      lastAt = e.timeStamp;
      const key = normaliseKey(e, gap);
      if (!key) return;

      if (key === "?") {
        e.preventDefault();
        toggleHotkeySheet();
        return;
      }
      // The sheet is a dialogue and answers for itself from here on.
      if (sheetOpen) return;

      const scope = topScope();
      if (!scope) return;

      if (key === "/") {
        const box = searchBoxIn(scope);
        if (!box) return;
        e.preventDefault();
        box.focus();
        if (box instanceof HTMLInputElement) box.select();
        return;
      }

      const hit = pickBinding(registry.list(), key, scope);
      if (!hit) return;
      e.preventDefault();
      hit.run();
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Collisions are a development-time report: two acts on one surface claiming
  // one letter means one of them can never be pressed, and the person who can
  // fix it is the person who just put them on the same screen.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const check = () => {
      for (const c of collisions(registry.list())) {
        console.warn(
          `[hotkeys] "${c.key}" is claimed by ${c.labels.length} acts on one surface: ${c.labels.join(" · ")}`,
        );
      }
    };
    const offRegistry = registry.subscribe(check);
    const offOverlays = subscribeOverlays(check);
    return () => {
      offRegistry();
      offOverlays();
    };
  }, []);
}
